<template>
  <div class="document-fill-step">
    <div v-if="loading" class="dfs-loading">
      <b-spinner small></b-spinner>
      <span>Cargando el documento…</span>
    </div>

    <div v-else-if="loadError" class="dfs-error">
      <p>{{ loadError }}</p>
      <button class="dfs-btn dfs-reset" @click="resetChat">Reiniciar chat</button>
    </div>

    <template v-else>
      <!-- Read-only page reference: shows the document's pages, the one being
           worked on, and which are done / left. Navigation is via the footer
           buttons, so these are not clickable. -->
      <!-- Read-only page progress (3 states: not-seen / current / seen).
           Styles are inline on purpose so they cannot be dropped by CSS
           extraction / caching; navigation is via the footer buttons only. -->
      <div
        v-if="!done && pageState.isPaged && pageNames.length"
        :style="stepperStyle"
      >
        <p :style="stepperTitleStyle">Hojas del documento</p>
        <div :style="stepperListStyle">
          <div
            v-for="(pname, pi) in pageNames"
            :key="pi"
            :style="stepStyle(pi)"
          >
            <span :style="stepNameStyle(pi)">{{ pname }}</span>
          </div>
        </div>
      </div>

      <DynamicDocumentForm
        v-if="!done"
        ref="form"
        :templateData="templateData"
        :school="schoolId"
        :grade="grade"
        :diagnostic="formDiagnostic"
        :fillFirstPage="fillFirstPage"
        :defaults="fieldDefaults()"
        :userSelect="userSelectData()"
        @page-state="pageState = $event"
      />

      <div v-if="done" class="dfs-done" :style="doneStyle">
        <p class="dfs-success">✓ ¡Tu documento está listo!</p>
        <p class="dfs-hint">
          Descárgalo aquí. Si lo necesitas, también puedes descargar el documento
          relacionado con los mismos datos. Puedes descargarlos las veces que
          quieras.
        </p>
        <button
          class="dfs-btn dfs-download dfs-redownload"
          :class="{ 'dfs-pulse': !downloadedPrimary }"
          :disabled="generating"
          @click="downloadAgain"
        >
          <b-spinner v-if="generating" small></b-spinner>
          <span>{{
            generating ? "Generando…" : "Descargar " + documentType.name
          }}</span>
        </button>
        <div v-if="relatedDocName" class="dfs-related">
          <p class="dfs-related-text">
            ¿Quieres descargar también
            <strong>{{ relatedDocName }}</strong> con los mismos datos?
          </p>
          <button
            class="dfs-btn dfs-download"
            :class="{ 'dfs-pulse': !downloadedRelated }"
            :disabled="generatingRelated"
            @click="downloadRelated"
          >
            <b-spinner v-if="generatingRelated" small></b-spinner>
            <span>{{
              generatingRelated ? "Generando…" : "Descargar " + relatedDocName
            }}</span>
          </button>
        </div>
        <FeedbackComponent :code="feedbackCode" />
      </div>

      <div class="dfs-footer">
        <button v-if="done" class="dfs-btn dfs-finish" @click="confirmReset">
          <span class="dfs-finish-ico">↻</span>
          Reiniciar chat
        </button>
        <template v-else>
          <span v-if="pageState.isPaged" class="dfs-page-indicator">
            Página {{ pageState.current + 1 }} de {{ pageState.total }}
          </span>
          <!-- Page navigation lives inside the embedded document (the
               "Página anterior / siguiente" buttons at the bottom of each
               page). On the last page we offer to move to the download view
               (the answers are captured here, the Word is generated there). -->
          <div class="dfs-footer-actions">
            <b-button
              v-if="isLastPage"
              variant="outline-primary"
              class="dfs-btn dfs-download"
              @click="goToDownload"
            >
              <span>Ir a descargar documento!</span>
            </b-button>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
// Custom chat-step: fetches the blank online template for the chosen
// (document type, grade, diagnosis), renders it in DynamicDocumentForm so the
// user answers it inline, then POSTs the collected field_values to be rendered
// to Word and downloaded. No Document is ever persisted (see backend
// render_individual_document). Mirrors ReportBottom's blob-download flow.
import DynamicDocumentForm from "./DynamicDocumentForm.vue";
import FeedbackComponent from "./FeedbackComponent.vue";
import individualDocumentService from "@/services/individualDocumentService";
import { buildRenderContext } from "./individualDocumentContext";

// After downloading a document whose name contains ALL of a rule's keywords,
// offer to also download `related` with the SAME data + same diagnostic.
// Matching is accent/punctuation-insensitive on WHOLE words, so it covers
// variants like "PAI Integral Online" or "Informe a la familia final (Formato
// Mineduc)", while keeping PAI and PACI distinct (different keyword).
const RELATED_RULES = [
  // New IF/FUReev pair: the trailing "." is what tells them apart from the old
  // ones, and docWords strips punctuation, so match the exact name.
  { name: "Informe a la familia final.", related: "FU de reevaluación online." },
  {
    keywords: ["informe", "familia", "final", "mineduc"],
    related: "Fu de reevaluación",
  },
  { keywords: ["paci", "online"], related: "Estado de avance" },
  { keywords: ["pai", "online"], related: "Estado de avance" },
];

// Today's date as dd/mm/yyyy (the document's "M" variable).
function todayDDMMYYYY() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

// Document name -> set of lowercased, accent-stripped, alphanumeric words.
function docWords(name) {
  return (name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

export default {
  name: "DocumentFillStep",
  components: { DynamicDocumentForm, FeedbackComponent },
  props: {
    context: { type: Object, required: true },
    canWork: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      loading: true,
      loadError: "",
      generating: false,
      generatingRelated: false,
      done: false,
      // Whether each downloadable document has actually been downloaded — used
      // to warn before restarting the chat if one is still pending.
      downloadedPrimary: false,
      downloadedRelated: false,
      templateData: null,
      // The render context built for the downloaded document, reused for the
      // related-document download.
      lastContext: null,
      // Page navigation state reported by DynamicDocumentForm. Non-paged
      // templates report isPaged=false (single view → download right away).
      pageState: { isPaged: false, current: 0, total: 1 },
      // Feedback code for individual-documents (see backend FeedbackEnum).
      feedbackCode: 5,
      // Inline styles for the read-only page stepper (kept inline so they're
      // immune to CSS extraction / caching issues — see template note).
      stepperStyle: {
        padding: "4px 2px 12px",
        marginBottom: "12px",
        borderBottom: "1px solid #eef0f3",
      },
      stepperTitleStyle: {
        margin: "0 0 10px",
        fontSize: "0.72rem",
        fontWeight: "800",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#1f3a5f",
      },
      stepperListStyle: {
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
      },
      // Centered download view (inline so it's immune to the CSS-build issue).
      doneStyle: {
        marginTop: "10px",
        textAlign: "center",
      },
    };
  },
  computed: {
    // Related document offered after this one is downloaded (or null): the
    // first rule whose keywords are all present in the document name.
    relatedDocName() {
      const words = docWords(this.documentType.name);
      const rule = RELATED_RULES.find((r) =>
        r.name
          ? (this.documentType.name || "").trim() === r.name
          : r.keywords.every((k) => words.includes(k))
      );
      return rule ? rule.related : null;
    },
    // Documents still pending download (offered but not yet downloaded). Used to
    // warn the user before restarting the chat so nothing is lost.
    pendingDownloads() {
      const pending = [];
      if (!this.downloadedPrimary) pending.push(this.documentType.name);
      if (this.relatedDocName && !this.downloadedRelated) {
        pending.push(this.relatedDocName);
      }
      return pending;
    },
    // Page names (display reference for the stepper).
    pageNames() {
      const tpl = this.templateData && this.templateData.document_template;
      const pages = (tpl && tpl.pages) || [];
      return pages.map((p, i) => (p && p.name) || `Página ${i + 1}`);
    },
    // Download is only offered on the last page (or for non-paged templates),
    // by which point every page has been visited and lazy-loaded.
    isLastPage() {
      return (
        !this.pageState.isPaged ||
        this.pageState.current >= this.pageState.total - 1
      );
    },
    documentType() {
      return (this.context && this.context.document_type) || {};
    },
    // Whether the user opted to fill the document's first (identification) page,
    // chosen in the `fill_first_page` chat question. Defaults to false (skip the
    // page) when unanswered.
    fillFirstPage() {
      const ans = this.context && this.context.fill_first_page;
      return !!(ans && ans.value === true);
    },
    grade() {
      const g = this.context && this.context.grade;
      return g && g.value != null ? g.value : null;
    },
    // The template was resolved either by the diagnosis step (each option
    // carries its template_id) or, when there was a single template, silently
    // by the grade step (document_type.resolvedTemplateId).
    templateId() {
      const d = this.context && this.context.diagnosis;
      if (d && d.templateId != null) return d.templateId;
      return this.documentType.resolvedTemplateId != null
        ? this.documentType.resolvedTemplateId
        : null;
    },
    // Diagnostic used for the modal's field serialization (special options /
    // page info). Prefer the value the backend resolved for the template.
    formDiagnostic() {
      if (this.templateData && this.templateData.diagnostic != null) {
        return this.templateData.diagnostic;
      }
      const d = this.context && this.context.diagnosis;
      if (d && d.value != null) return d.value;
      return this.documentType.resolvedDiagnostic != null
        ? this.documentType.resolvedDiagnostic
        : null;
    },
    schoolId() {
      if (this.templateData && this.templateData.school_id != null) {
        return this.templateData.school_id;
      }
      if (this.documentType.schoolId != null) return this.documentType.schoolId;
      return this.context && this.context.schoolId != null
        ? this.context.schoolId
        : null;
    },
  },
  mounted() {
    this.loadTemplate();
  },
  methods: {
    // Auto-filled document variables, keyed by field key. A METHOD (not a
    // computed) so it's always fresh — the chat values (e.g. the student name)
    // are added to `context` non-reactively, which a computed could cache.
    //   H  -> the selected grade's text
    //   M  -> today's date (dd/mm/yyyy)
    //   AM -> the selected diagnosis text (only when the user actually chose
    //         one; a single-diagnostic template resolves silently → null)
    //   NYA_E -> the student name (only when the first page was skipped)
    fieldDefaults() {
      const defaults = { M: todayDDMMYYYY() };
      const grade = this.context && this.context.grade;
      if (grade && grade.name) defaults.H = grade.name;
      const diagnosis = this.context && this.context.diagnosis;
      if (diagnosis && diagnosis.name) defaults.AM = diagnosis.name;
      if (!this.fillFirstPage) {
        const sn = this.context && this.context.student_name;
        const name = sn && sn.textInput;
        if (name) defaults.NYA_E = name;
      }
      return defaults;
    },
    // The logged-in user offered as the option of the USER_SELECTOR matching
    // the role they chose in the chat (`user_role`). Returns null when no role
    // was indicated or the user data hasn't loaded. The rendered `role` text is
    // the chosen role's label (not the user's stored role). A METHOD so it's
    // always fresh (the chat values are added to `context` non-reactively).
    userSelectData() {
      const ur = this.context && this.context.user_role;
      const cu = this.templateData && this.templateData.current_user;
      if (!ur || ur.value == null || !cu || cu.id == null) return null;
      const data = Object.assign({}, cu, { role: ur.name });
      return { role: ur.value, data };
    },
    loadTemplate() {
      this.loading = true;
      this.loadError = "";
      individualDocumentService
        .getBlankTemplate({
          template_id: this.templateId,
          // fallback resolution when no template_id was pre-resolved
          document_type_id: this.documentType.id,
          diagnostic: this.formDiagnostic,
          grade: this.grade,
        })
        .then((data) => {
          this.templateData = data;
        })
        .catch((error) => {
          this.loadError =
            "No se pudo cargar el documento. " +
            (error.response && error.response.data
              ? error.response.data.message || ""
              : "");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Capture the answers into the final render context and move to the
    // download view. The context MUST be built here, while the form is still
    // mounted (it's unmounted once `done`); the Word is generated on demand in
    // the next view (downloadAgain / downloadRelated), so the same data can be
    // downloaded as many times as needed.
    goToDownload() {
      const form = this.$refs.form;
      if (!form) return;
      // Resolve the answers to the final docxtpl context on the client (the
      // template carries every option text / table title / OA tag it needs);
      // the backend just drops this into the template and renders.
      const context = buildRenderContext(
        this.templateData.document_template,
        form.getFieldValues(),
        this.documentType.name,
        this.schoolId
      );
      // Force the auto-fill variables into the FINAL context. Doing it here
      // (not just via the in-form seeding) guarantees they land even if their
      // field's page was dropped or loaded blank — read straight from the chat
      // context so a late value (e.g. the student name) is never missed.
      const defaults = this.fieldDefaults();
      Object.keys(defaults).forEach((k) => {
        if (defaults[k] != null && defaults[k] !== "") {
          if (context[k] == null || context[k] === "") context[k] = defaults[k];
        }
      });
      // Keep the built context for the primary + related downloads.
      this.lastContext = context;
      this.done = true;
    },
    // Render the primary document from the context built in goToDownload. Can be
    // run repeatedly (e.g. if the browser blocked the download); the form is
    // already unmounted in this view, so it reuses lastContext.
    downloadAgain() {
      if (!this.lastContext) return;
      this.generating = true;
      individualDocumentService
        .renderDocument({
          template_id: this.templateData.template_id,
          document_type_id: this.documentType.id,
          school_id: this.schoolId,
          context: this.lastContext,
        })
        .then((response) => {
          this.saveBlob(response, this.documentType.name);
          this.downloadedPrimary = true;
        })
        .catch((error) => this.handleRenderError(error))
        .finally(() => {
          this.generating = false;
        });
    },
    // Renders a related document (see RELATED_DOCUMENTS) reusing the context
    // already built for the downloaded document, resolving the related
    // template by name + the same diagnostic.
    downloadRelated() {
      if (!this.relatedDocName || !this.lastContext) return;
      this.generatingRelated = true;
      individualDocumentService
        .resolveRelatedDocument({
          document_name: this.relatedDocName,
          diagnostic: this.formDiagnostic,
        })
        .then((res) =>
          individualDocumentService.renderDocument({
            template_id: res.template_id,
            document_type_id: res.document_type_id,
            school_id: res.school_id != null ? res.school_id : this.schoolId,
            context: this.lastContext,
          })
        )
        .then((response) => {
          this.saveBlob(response, this.relatedDocName);
          this.downloadedRelated = true;
        })
        .catch((error) => this.handleRenderError(error))
        .finally(() => {
          this.generatingRelated = false;
        });
    },
    saveBlob(response, fallbackName) {
      let filename =
        ((fallbackName || "documento") + "").replace(/\s+/g, "_") + ".docx";
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("attachment") !== -1) {
        const m = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (m != null && m[1]) filename = m[1].replace(/['"]/g, "");
      }
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    },
    handleRenderError(error) {
      // Out of document uses (403 {documents: "no_uses"}); the body is a Blob
      // here (responseType blob), so key off the status code.
      if (error.response && error.response.status === 403) {
        this.$toasted.error(
          "No cuentas con usos suficientes para descargar el documento."
        );
      } else {
        this.$toasted.error(
          "Error al generar el documento: " +
            (error.response ? error.response.status : error.message)
        );
      }
    },
    resetChat() {
      this.$emit("go-to-step", "reset");
    },
    // Restart the chat, but warn first when a downloadable document is still
    // pending — the user may have forgotten to download it (it can't be
    // recovered after the reset).
    confirmReset() {
      const pending = this.pendingDownloads;
      if (!pending.length) {
        this.resetChat();
        return;
      }
      const list = pending.join(" y ");
      this.$bvModal
        .msgBoxConfirm(
          `Aún no has descargado: ${list}. Si reinicias el chat perderás ` +
            `el documento y deberás completarlo de nuevo. ¿Quieres reiniciar de todas formas?`,
          {
            title: "Tienes documentos sin descargar",
            okTitle: "Reiniciar de todas formas",
            cancelTitle: "Volver",
            okVariant: "danger",
            centered: true,
          }
        )
        .then((confirmed) => {
          if (confirmed) this.resetChat();
        })
        .catch(() => {});
    },
    // Per-page card style by state: not-seen (default) / seen / current.
    stepStyle(pi) {
      const style = {
        flex: "1 1 120px",
        minWidth: "110px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "12px 14px",
        borderRadius: "12px",
        background: "#fff",
        border: "1.5px solid #e5e7eb",
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
        cursor: "default",
        userSelect: "none",
      };
      if (pi === this.pageState.current) {
        style.background = "#fff7e8";
        style.border = "2px solid #f8b500";
        style.boxShadow = "0 2px 6px rgba(248,181,0,0.18)";
      } else if (pi < this.pageState.current) {
        style.background = "#fffdf8";
        style.border = "1.5px solid #fcd9a0";
      }
      return style;
    },
    stepNameStyle(pi) {
      let color = "#9aa3af"; // not-seen
      if (pi === this.pageState.current) color = "#1f2937";
      else if (pi < this.pageState.current) color = "#6b7280";
      return {
        fontSize: "0.74rem",
        fontWeight: "700",
        letterSpacing: "0.04em",
        lineHeight: "1.25",
        textTransform: "uppercase",
        color,
      };
    },
  },
};
</script>

<style scoped>
.document-fill-step {
  width: 100%;
  padding: 6px 0;
}
/* The page stepper is styled inline (see template) so it survives CSS
   extraction / caching; no rules for it here on purpose. */
.dfs-loading,
.dfs-error {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  padding: 12px;
}
.dfs-error {
  flex-direction: column;
  align-items: flex-start;
}
.dfs-done {
  margin-top: 10px;
}
.dfs-success {
  font-weight: 600;
  color: #f8b500;
  margin-bottom: 4px;
}
.dfs-hint {
  color: #6b7280;
  font-size: 0.9rem;
  margin-bottom: 8px;
}
.dfs-redownload {
  margin-bottom: 4px;
}
.dfs-related {
  margin: 10px 0;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 10px;
}
.dfs-related-text {
  margin-bottom: 8px;
  color: #374151;
}
.dfs-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
}
.dfs-page-indicator {
  font-weight: 600;
  color: #6b7280;
}
.dfs-footer-actions {
  display: flex;
  gap: 10px;
  margin-left: auto;
}
.dfs-nav {
  background: #6b7280;
}
.dfs-next {
  background: #2563eb;
}
.dfs-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
}
/* Download buttons use the document's accent yellow (matches the in-document
   buttons + the page stepper). */
.dfs-download {
  background: #f8b500;
  color: #4a3700;
}
.dfs-download:hover {
  filter: brightness(1.04);
}
/* Pulse a download button until it has actually been downloaded. */
.dfs-pulse {
  animation: dfs-pulse 1.6s ease-out infinite;
}
@keyframes dfs-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(248, 181, 0, 0.5);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(248, 181, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(248, 181, 0, 0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .dfs-pulse {
    animation: none;
  }
}
/* Restart: a refined outlined pill, pushed to the right of the footer. */
.dfs-finish {
  margin-left: auto;
  background: #fff;
  color: #6b7280;
  border: 1.5px solid #e5e7eb;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  transition: color 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}
.dfs-finish:hover {
  background: #fffdf8;
  border-color: #f8b500;
  color: #b27600;
}
.dfs-finish-ico {
  font-size: 1.05rem;
  line-height: 1;
}
.dfs-reset {
  background: #ef4444;
}
.dfs-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
